// ============================================================================
// client/entree-apercu.ts — VOIR UNE ENTRÉE EN SCÈNE HORS DU JEU
// ----------------------------------------------------------------------------
// Deux endroits en ont besoin, et c'est pour cela que ce module existe plutôt
// que deux copies : le VESTIAIRE (choisir celle qu'on porte) et la CARTE DE
// DÉBLOCAGE (au moment où on vient de la gagner).
//
// ⚠️ Une vignette d'entrée en scène ne peut PAS être une image fixe. Ces six
// animations ne sont faites que de mouvement : une capture de « la réception »
// à un instant quelconque montre un bonhomme accroupi, et « la lampe » montre
// un carré noir. Le vestiaire les JOUE donc, en boucle, en petit.
//
// Les deux économies qui rendent ça gratuit :
//  · le décor est peint UNE fois par taille, puis recopié (comme le jeu le fait
//    de son terrain) ;
//  · une vignette hors de l'écran ne peint pas (IntersectionObserver). Six
//    aperçus dans une liste déroulante ne coûtent donc que ceux qu'on regarde.
// ============================================================================

import { ENTREE_MS, peintEntree } from './entree-scene'
import { equipped } from './loadout'
import { avatarHue } from './prefs'
import { hueIndex } from '../shared/skins'
import { setDetail } from './papier'
import { BIOMES, tuilePapier } from './terrain-papier'
import { entreeById } from '../shared/entrees'

/* L'aperçu peint à la taille du JEU (case de 48, héros de 44) dans un canvas
   hors écran, puis réduit : le geste du navigateur. Une animation repeinte plus
   petite montrerait des trajectoires et des particules que personne ne verra. */
const CASE = 48
// ⚠️ DIX cases sur CINQ, pas douze sur sept. Le premier cadrage donnait un rapport 12:7 : dans
// une colonne de vestiaire large de 800 pixels, chaque carte mesurait 470 pixels de haut et une
// seule tenait à l'écran. Un choix ne se fait pas en faisant défiler. Le rapport 2:1 laisse six
// cartes lisibles, et il suffit largement : toutes les entrées se jouent autour du héros.
const APERCU_W = 10 * CASE, APERCU_H = 5.5 * CASE   // 480 × 264
// Le sol à l'avant-DERNIÈRE rangée : à SOL_ROW = 3, briques et béton mangeaient 40 % de la
// vignette pour ne rien montrer. Quatre entrées sur six arrivent par le haut — c'est le ciel
// qu'il faut leur donner. La demi-rangée sous le sol reste, elle sert au percement.
const SOL_ROW = 4
const PAUSE = 600

const cache = new Map<string, HTMLCanvasElement>()

/** Un bout de galerie : deux rangées de briques et le ciel de la grotte. Le
 *  strict nécessaire pour qu'une arrivée ait un sol et un fond. */
function decorApercu(): HTMLCanvasElement {
  const vieux = cache.get('d')
  if (vieux) return vieux
  const cv = document.createElement('canvas')
  cv.width = APERCU_W; cv.height = APERCU_H
  cache.set('d', cv)
  const g = cv.getContext('2d')
  if (!g) return cv
  setDetail(1)
  const grd = g.createLinearGradient(0, 0, 0, APERCU_H)
  grd.addColorStop(0, '#241d3a'); grd.addColorStop(1, '#171227')
  g.fillStyle = grd; g.fillRect(0, 0, APERCU_W, APERCU_H)
  const P = BIOMES.grotte
  for (let c = 0; c < 10; c++) {
    tuilePapier(g, c * CASE, SOL_ROW * CASE, CASE, 'Brick', P)
    tuilePapier(g, c * CASE, (SOL_ROW + 1) * CASE, CASE, 'Concrete', P)
  }
  return cv
}

/** Une seule génération d'aperçus peint : un vestiaire rouvert ne doit pas laisser tourner les
 *  boucles du précédent.
 *  ⚠️ `apercuEntree` LIT ce compteur, il ne l'incrémente pas — sinon chaque aperçu invaliderait
 *  le précédent et SEUL LE DERNIER peindrait. Écrit d'abord dans l'autre sens, et c'est
 *  exactement le bug qui avait laissé cinq vignettes noires sur six dans la planche : le même
 *  piège, deux fois, à deux heures d'intervalle. */
let generation = 0
export function stopApercus(): void { generation++ }

/**
 * Monte un aperçu animé dans `cv`. Le canvas prend sa taille CSS ; la hauteur
 * est imposée par le rapport de la scène. Rend une fonction d'arrêt.
 */
export function apercuEntree(cv: HTMLCanvasElement, id: string): void {
  const mien = generation
  const dpr = Math.min(2, window.devicePixelRatio || 1)
  const w = cv.clientWidth || 260
  const h = Math.round(w * (APERCU_H / APERCU_W))
  cv.width = Math.round(w * dpr); cv.height = Math.round(h * dpr)
  cv.style.height = h + 'px'
  const g = cv.getContext('2d')
  if (!g) return
  g.setTransform(dpr, 0, 0, dpr, 0, 0)

  const off = document.createElement('canvas')
  off.width = APERCU_W; off.height = APERCU_H
  const og = off.getContext('2d')
  if (!og) return

  let visible = true
  new IntersectionObserver((es) => { visible = es[0]?.isIntersecting ?? true }, { rootMargin: '80px' }).observe(cv)

  let t0 = 0
  const image = (now: number): void => {
    if (mien !== generation || !cv.isConnected) return
    requestAnimationFrame(image)
    if (!visible) { t0 = 0; return }
    if (!t0) t0 = now
    const ms = Math.min(ENTREE_MS, (now - t0) % (ENTREE_MS + PAUSE))
    og.setTransform(1, 0, 0, 1, 0, 0)
    og.clearRect(0, 0, APERCU_W, APERCU_H)
    og.drawImage(decorApercu(), 0, 0)
    peintEntree(og, APERCU_W, APERCU_H, ms, id, {
      cx: APERCU_W / 2, ySol: SOL_ROW * CASE,
      hue: hueIndex(avatarHue()), outfit: equipped('outfit') ?? '', hat: equipped('hat') ?? '',
    })
    g.clearRect(0, 0, w, h)
    g.drawImage(off, 0, 0, APERCU_W, APERCU_H, 0, 0, w, h)
  }
  requestAnimationFrame(image)
}

/* ══════════════════════════════════════════════════ LA CARTE DE DÉBLOCAGE */

/**
 * L'instant où l'on gagne. Une carte qui monte, l'animation qui joue dedans, et
 * le nom de la campagne qu'on vient de finir.
 *
 * ⚠️ Elle se ferme au clic OU toute seule après douze secondes, et JAMAIS elle
 * ne bloque : elle arrive pendant l'écran de fin de campagne, qui attend déjà un
 * geste. Un overlay qu'il faut fermer pour continuer transformerait une
 * récompense en corvée.
 */
export function montreEntreeGagnee(id: string, campagne: string): void {
  const e = entreeById(id)
  if (!e) return
  document.getElementById('entree-gagnee')?.remove()

  const fond = document.createElement('div')
  fond.id = 'entree-gagnee'
  fond.style.cssText = [
    'position:fixed', 'inset:0', 'z-index:60', 'display:flex',
    'align-items:center', 'justify-content:center', 'padding:20px',
    'background:rgba(6,4,14,.72)', 'backdrop-filter:blur(3px)',
    'font-family:ui-monospace,Menlo,monospace', 'animation:eg-fond .45s ease',
  ].join(';')

  const carte = document.createElement('div')
  carte.style.cssText = [
    'width:min(560px,100%)', 'background:linear-gradient(180deg,#1d1830,#141022)',
    'border:1px solid #ffd23f66', 'border-radius:18px', 'overflow:hidden',
    'box-shadow:0 30px 90px -20px #000, 0 0 0 1px #ffffff0a inset',
    'animation:eg-carte .5s cubic-bezier(.2,.9,.3,1.2)',
  ].join(';')

  const titre = document.createElement('div')
  titre.style.cssText = 'padding:18px 22px 12px;text-align:center'
  const sur = document.createElement('div')
  sur.textContent = 'ENTRÉE EN SCÈNE DÉBLOQUÉE'
  sur.style.cssText = 'color:#ffd23f;font-size:11px;letter-spacing:.24em;margin-bottom:8px'
  const nom = document.createElement('div')
  nom.textContent = e.nom
  nom.style.cssText = 'color:#fff3c4;font-size:25px;font-weight:700;letter-spacing:-.01em'
  titre.append(sur, nom)

  const cv = document.createElement('canvas')
  cv.style.cssText = 'display:block;width:100%;background:#171227'

  const bas = document.createElement('div')
  bas.style.cssText = 'padding:15px 22px 20px;text-align:center'
  const quoi = document.createElement('div')
  quoi.textContent = e.desc
  quoi.style.cssText = 'color:#d9d4ea;font-size:13.5px;line-height:1.6;margin-bottom:10px'
  const dou = document.createElement('div')
  /* textContent, jamais innerHTML : le nom d'une campagne est du texte de
     joueur, sans filtre de charset côté serveur (même règle que campaigns-ui). */
  dou.textContent = `Campagne « ${campagne} » terminée en solo · à équiper au vestiaire`
  dou.style.cssText = 'color:#8b83a8;font-size:12px'
  bas.append(quoi, dou)

  carte.append(titre, cv, bas)
  fond.append(carte)
  const style = document.createElement('style')
  style.textContent = '@keyframes eg-fond{from{opacity:0}to{opacity:1}}'
    + '@keyframes eg-carte{from{opacity:0;transform:translateY(26px) scale(.97)}to{opacity:1;transform:none}}'
  fond.append(style)
  document.body.append(fond)

  /* la largeur CSS n'est connue qu'une fois dans le document */
  apercuEntree(cv, id)

  const fermer = (): void => { window.clearTimeout(minuteur); fond.remove() }
  fond.addEventListener('click', fermer)
  const minuteur = window.setTimeout(fermer, 12_000)
}
